import { Text } from '@cash-me/react-components'
import styled, { css, keyframes } from 'styled-components'

import { Polygon as PolygonComponent } from 'components/polygon'
import { theme } from 'styles/theme'

const { media, colors, space, zIndices, fontSizes, radii, shadows } = theme

type VariantProps = {
  readonly $variant: 'default' | 'land-loan' | 'home-renovation' | 'construction-loan' | 'solar-energy'
}

const showFromLeft = keyframes`
  from {
    transform: translateX(-100%);
    opacity: 0;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
`

const showFromRight = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
`

export const Hero = styled.div<VariantProps>`
  position: relative;
  z-index: ${zIndices[1]};
  background-color: ${({ $variant }) =>
    $variant === 'home-renovation' ? '#f4f6fb' : colors['neutral-background-contrast']};
  width: 100%;

  & > div > div:first-child {
    animation: ${showFromLeft} 1.5s;
  }

  /* A decoração neon é ancorada na coluna do formulário: a coluna acompanha a
     altura da linha do grid, então a linha pode ser medida a partir da seção
     em vez da altura do card. */
  & > div > div:last-child {
    position: relative;
  }

  @media (${media.phone}) {
    padding-top: ${({ $variant }) => ($variant === 'home-renovation' ? space['spacing-xxxl'] : space['spacing-lg'])};

    > div {
      padding: 0;

      /* Reforma empilha texto e simulador no mobile; o vão de 2rem afastava
         demais um do outro. */
      ${({ $variant }) =>
        $variant === 'home-renovation' &&
        css`
          row-gap: ${space['spacing-md']};
        `}
    }

    > div > div:first-child {
      padding: 0 ${space['spacing-md']} 0;
    }
  }

  @media (${media.tablet}) {
    overflow: clip visible;
    padding-top: ${space['spacing-giga']};
    padding-bottom: ${space['spacing-xxxl']};

    & > div > div:last-child {
      animation: ${showFromRight} 1.5s;
    }
  }
`

export const Infos = styled.div<VariantProps>`
  z-index: ${zIndices[1]};
  margin-bottom: ${({ $variant }) => ($variant === 'home-renovation' ? 0 : space['spacing-md'])};

  h1 {
    margin-bottom: ${space['spacing-lg']};
  }

  > p {
    margin-bottom: ${space['spacing-md']};
  }

  @media (${media.phone}) {
    ${({ $variant }) =>
      $variant === 'home-renovation' &&
      css`
        > div:first-child,
        h1 {
          margin-bottom: ${space['spacing-md']};
        }

        > p {
          margin-bottom: ${space['spacing-xl']};
          font-size: 1rem;
        }

        ul {
          gap: ${space['spacing-md']};
        }

        ul p {
          font-size: ${fontSizes['font-size-body-small']};
        }
      `}
  }

  @media (${media.tablet}) {
    margin-top: ${({ $variant }) => ($variant === 'home-renovation' ? 0 : space['spacing-ultra'])};
  }

  @media (${media.desktop}) {
    h1,
    h1 span {
      font-size: ${fontSizes['font-size-heading-2']};
    }

    > p {
      font-size: ${fontSizes['font-size-body-small']};
    }

    ${({ $variant }) =>
      $variant === 'home-renovation' &&
      css`
        h1 {
          margin-bottom: ${space['spacing-xl']};
        }

        > div:first-child,
        > p {
          margin-bottom: ${space['spacing-xl']};
        }
      `}
  }
`

const titleHighlightColor = (variant: VariantProps['$variant']) => {
  if (variant === 'home-renovation') return colors['cashme-magenta-alt']
  if (variant === 'land-loan') return colors['cyan-text-contrast']

  return colors['cyan-background-pressed']
}

export const TitleHighlight = styled.span<VariantProps>`
  color: ${({ $variant }) => titleHighlightColor($variant)};
`

export const LoanInfos = styled.ul`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${space['spacing-xs']};

  @media (${media.tablet}) {
    gap: ${space['spacing-sm']};
    margin-bottom: ${space['spacing-xl']};
  }

  @media (${media.desktop}) {
    gap: ${space['spacing-md']};
  }
`

export const LoanInfosItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${space['spacing-sm']};

  @media (${media.tablet}) {
    p {
      font-size: ${fontSizes['font-size-body-small']};
    }
  }

  > svg {
    margin-bottom: ${space['spacing-xxs']};

    @media (${media.tablet}) {
      margin-bottom: 0;
    }
  }
`

export const RightWrapper = styled.div`
  position: relative;

  @media (${media.tablet}) {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`

export const PolygonWrapper = styled.div`
  display: none;
  position: absolute;
  z-index: 0;
  top: 0;
  left: 0;

  &::after {
    content: '';
    display: block;
    position: absolute;
    width: 50vw;
    height: 200vh;
    top: -50px;
    left: 0;
    border-top: 3px solid ${colors['cyan-background-pressed']};
    border-left: 3px solid ${colors['cyan-background-pressed']};
    border-top-left-radius: 150px;
    z-index: 1;
  }

  @media (${media.tablet}) {
    display: flex;
    bottom: -${space['spacing-xxxl']};

    &::after {
      top: -50px;
      height: calc(100% + 50px);
    }
  }

  @media (${media.desktop}) {
    &::after {
      left: -50px;
    }
  }
`

export const Polygon = styled(PolygonComponent)`
  width: 90px;
  height: 110px;
  position: relative;
  right: 50%;
  top: 50px;
  z-index: 2;

  @media (${media.desktop}) {
    right: 100%;
  }
`

export const FormWrapper = styled.form<VariantProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${space['spacing-sm']};
  z-index: ${zIndices[2]};
  background-color: ${colors['neutral-background-default']};
  padding: ${space['spacing-lg']};
  border-top-right-radius: ${radii['corner-radius-md']};
  border-top-left-radius: ${radii['corner-radius-md']};
  box-shadow: ${shadows['shadow-level-2']} #00000029;

  ${({ $variant }) =>
    $variant === 'home-renovation' &&
    css`
      gap: ${space['spacing-md']};
      padding: ${space['spacing-md']};
      border-top-right-radius: ${space['spacing-xl']};
      border-top-left-radius: ${space['spacing-xl']};
      box-shadow: 0 0 10px rgb(0 0 0 / 4%);
    `}

  .ds-button {
    width: 100%;
    justify-content: center;
    border-radius: ${radii['corner-radius-pill']};
  }

  @media (${media.tablet}) {
    max-width: ${({ $variant }) => ($variant === 'home-renovation' ? '430px' : '420px')};
    gap: ${space['spacing-md']};
    padding: ${space['spacing-md']};
    margin: ${space['spacing-xl']} ${space['spacing-xxl']};
    border-radius: ${({ $variant }) =>
      $variant === 'home-renovation' ? radii['corner-radius-lg'] : radii['corner-radius-md']};

    ${({ $variant }) =>
      $variant === 'home-renovation' &&
      css`
        margin: 0;
        box-shadow: 0 2px 7px rgb(0 0 0 / 5%);
      `}
  }
`

export const FormHeading = styled(Text)`
  display: flex;
  gap: ${space['spacing-sm']};
  align-items: center;

  span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${space['spacing-md']};
    height: ${space['spacing-md']};
    padding: ${space['spacing-sm']};
    border-radius: ${radii['corner-radius-circular']};
    background: ${colors['blue-background-strong-default']};
    color: ${colors['blue-text-strong']};
    font-size: ${fontSizes['font-size-heading-5']};
  }
`

export const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;

  small {
    margin-bottom: calc(${space['spacing-xxxs']} + 1px);
  }

  > * {
    padding: 0;
    margin: 0;
  }

  > div:last-of-type > div:only-child {
    margin: 0;
  }

  @media (${media.tablet}) {
    gap: ${space['spacing-xs']};
  }
`

export const RadioWrapper = styled.div`
  display: flex;
  flex-direction: column;

  > label {
    margin-bottom: 0;
  }
`
